import type { ReactNode } from "react";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { Button } from "./Button.tsx";
import { AlertIcon } from "./Icons.tsx";

/**
 * The three things a screen shows when it has no content: loading, nothing, or broken.
 *
 * They share a silhouette on purpose. The skeleton is the shape of the cards that are coming, so
 * the layout does not jump when they arrive, and the empty and error states occupy the same block
 * so a screen never resizes as it settles.
 */

export function SkeletonList({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2.5" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-start gap-3 rounded-card border border-line bg-paper p-3.5"
        >
          <div className="flex w-14 shrink-0 flex-col items-end gap-1.5">
            <span className="h-4 w-11 animate-pulse rounded bg-sunken" />
            <span className="h-2.5 w-6 animate-pulse rounded bg-sunken" />
          </div>
          <span className="w-[3px] self-stretch rounded-full bg-sunken" />
          <div className="flex flex-1 flex-col gap-2">
            <span className="h-3.5 w-2/3 animate-pulse rounded bg-sunken" />
            <span className="h-3 w-1/2 animate-pulse rounded bg-sunken" />
            <span className="h-4 w-20 animate-pulse rounded-pill bg-sunken" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  icon,
  action,
  compact = false,
}: {
  title: string;
  message: string;
  icon?: ReactNode;
  action?: ReactNode;
  /**
   * For an empty section inside a longer scroll, rather than an empty screen.
   *
   * A business that sells no packages and has taken no payments would otherwise get two full-height
   * hero panels in a row on the More tab, which gives the two things it does not do more of the
   * screen than the things it does. Same words, a quarter of the room.
   */
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="rounded-card border border-dashed border-line-strong bg-paper/60 px-4 py-4">
        <p className="text-[14px] font-semibold text-ink">{title}</p>
        <p className="mt-0.5 text-[13px] text-ink-2">{message}</p>
        {action ? <div className="mt-3">{action}</div> : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center rounded-card border border-dashed border-line-strong bg-paper/60 px-6 py-12 text-center">
      {icon ? <div className="mb-3 text-ink-3">{icon}</div> : null}
      <h2 className="text-[19px] font-semibold text-ink">{title}</h2>
      <p className="mt-2 max-w-[30ch] text-[14px] text-ink-2">{message}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const copy = copyForError(error);
  return (
    <div
      role="alert"
      className="flex flex-col items-center rounded-card border border-line bg-paper px-6 py-10 text-center shadow-card"
    >
      <span className="mb-3 flex size-11 items-center justify-center rounded-full bg-cancelled-surface text-cancelled">
        <AlertIcon size={22} />
      </span>
      <h2 className="text-[19px] font-semibold text-ink">{copy.title}</h2>
      <p className="mt-2 max-w-[32ch] text-[14px] text-ink-2">{copy.message}</p>
      {onRetry ? (
        <Button variant="secondary" className="mt-5" onClick={onRetry}>
          {copy.action ?? "Try again"}
        </Button>
      ) : null}
    </div>
  );
}
